import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient } from '@angular/common/http';

import { LoginVm } from '../../../shared/generated/Models/LoginVm';
import { SecurityService } from '../../../core/security/security.service';
import { SystemMessageService, SystemMessage } from '../../system-message/system-message-service';
import { validateForm } from '../../../shared/form-elements/form-validateForm.function';
import { WebAuthnService } from '../../security/multi-factor-auth/web-authn.service';
import { MultiFactorAuthModalComponent } from './multi-factor-auth-modal/multi-factor-auth-modal.component';
import { UserJsVm } from '../../../shared/generated/Administration/Models/User/JavaScript/UserJsVm';
import { GlobalService } from '../../../shared/service/global.service';
import { GlobalVariablesService } from '../../../services/global-variables.service';

@Component({
    selector: 'pcg-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit {

	@ViewChild('myForm', { static: true }) myForm: ElementRef;

	loginForm = LoginVm.Form;
	user: UserJsVm;
	sm: SystemMessage;

	isAdmin = false;
	isUsingSsoLogin = false;
	isNewUser: boolean;	
	isError = false;

	errorString = "";
	ssoEmail: string = "";	
	ssoCode = "";

	constructor(
		private route: ActivatedRoute
		, private ms: SystemMessageService
		, private sec: SecurityService
		, private router: Router
		, private modalService: NgbModal
		, private webAuthn: WebAuthnService
		, private gvs: GlobalVariablesService
		, private httpClient: HttpClient
	) { }

	ngOnInit() {
		const user = this.sec.getUser();
		if (user) { this.loginRedirect(); }
		this.gvs.isNavFixed.set(false);

		this.isUsingSsoLogin = this.route.snapshot.queryParams.useSso != null 
			? this.route.snapshot.queryParams.useSso === "true"
			: false;

        if (this.route.snapshot.queryParams.errorMessage != null) { this.ms.setSystemMessage(decodeURIComponent(this.route.snapshot.queryParams.errorMessage)); }
        if (this.route.snapshot.queryParams.userIdFromSso != null) { this.getUserObjAndLogin(); }
	}

	switchSso() { this.isUsingSsoLogin = !this.isUsingSsoLogin; }

	onSubmit() {
		if (this.loginForm.valid) {
			this.sec.setLocalStorage('lastAccess', new Date());
			this.ms.getHttpObservable(this, 'api/Account/Login', this.loginForm)
				.subscribe(msg => { this.doLogin(msg); });
		} else { validateForm(this.loginForm); }
	}

	doLogin(msg: SystemMessage) {
		try {
			const val = msg.value;
			if (!val) { return; }
			if (val.hasTwoFactorAuth) {
				// Need this hack to avoid change detection error on modal open
				this.myForm.nativeElement.ownerDocument.activeElement.blur();
				// Open the 2FA modal
				this.modalService.open(MultiFactorAuthModalComponent);
				// Handle 2FA
				this.doTwoFactorAuth();
				return;
			}
			this.loginForm.reset();
			this.sec.setSecurity(val.token, val.user, val.security);
			this.loginRedirect();
		} catch (ex: any) { throw ex; }		
	}

    ssoLogin() {
		this.httpClient.get(`api/Account/SsoLogin?email=${encodeURIComponent(this.ssoEmail)}`)
			.subscribe((sm: any) => { 
				if (sm.isSuccessful) { window.location.href = sm.value; } 
				else { this.ms.setSystemMessage(sm.message, "error"); }
			}
		);
	}

    getUserObjAndLogin() {
        var userId = Number(this.route.snapshot.queryParams.userIdFromSso);
        this.httpClient.get(`api/Account/GetUserObjectFromId?userId=${userId}`).subscribe((msg: any) => {
            if (!msg.isSuccessful) { this.ms.setSystemMessage(msg.message, msg.messageClass); }
            else {
                this.loginForm.reset();
                this.sec.setSecurity(msg.value.token, msg.value.user, msg.value.security);
                localStorage.setItem("loggedInUsingSso", "true");
                this.loginRedirect();
            }
        })
    }

	loginRedirect() {
		this.user = this.sec.getUser();
		this.isNewUser = this.user?.isNewUser;
		this.isAdmin = this.sec.hasAccess(o => o.adminFeatures);
		GlobalService.eventize(this.user);
		let redirectUrl = this.route.snapshot.queryParamMap.get('redirectUrl') || sessionStorage.getItem('redirectUrl');

		// Remember if nav is docked
		if (localStorage.getItem("isNavFixed") === "true") { this.gvs.isNavFixed.set(true); }	

		if (this.isNewUser === true) { this.router.navigateByUrl('/users/edit/security/' + this.user?.id); } 
		else if (redirectUrl) {
			const baseUrl = document.getElementsByTagName('base')[0].href;
			redirectUrl = redirectUrl.replace(/^\//, '');
			if (
				redirectUrl.startsWith('uploads/') 
				|| redirectUrl.startsWith('quartz')
			) { window.location.href = baseUrl + redirectUrl; } 
			else {
				this.router.navigateByUrl(redirectUrl);
				sessionStorage.removeItem('redirectUrl');
			}
		} else {
			if (!this.isAdmin) { this.router.navigate(['/sites/by-event']); } 
			else { this.router.navigate(['/dashboard']); }			
		}
	}

	doTwoFactorAuth() {
		if (!this.webAuthn.isFidoCompatible()) {
			this.modalService.dismissAll();
			return;
		}

		// Save user credentials and clear form
		const userName = this.loginForm.value.userName;
		const password = this.loginForm.value.password;
		this.loginForm.reset();

		// Get assertion options from the server for user
		this.webAuthn.getAssertionOptions(userName, password)
			.subscribe(makeAssertionOptions => {
				navigator['credentials']
					.get({ publicKey: makeAssertionOptions })
					.then(credential => {
						this.sec.setLocalStorage('lastAccess', new Date());
						this.webAuthn.verifyAssertion(credential, userName, password)
							.subscribe((msg: SystemMessage) => {
								this.modalService.dismissAll();
								this.doLogin(msg);
							});
					}).catch(err => {
						this.modalService.dismissAll();
						// Do this so there is a failed login history rercord:
						this.webAuthn.verifyAssertion(null, userName, password)
							.subscribe((msg: SystemMessage) => { this.doLogin(msg); });
					}
				);
			}
		);
    }
}